<script setup>
import { RouterView } from 'vue-router'
import MsgToast from './components/common/MsgToast.vue'
import { inject, onMounted, onBeforeMount, onBeforeUnmount } from 'vue'
import { usePortAllData } from '@/stores/portAllData'
import { storeToRefs } from 'pinia'
import { toast } from 'vue3-toastify'
import { useI18n } from 'vue-i18n';
import { httpRequest } from '@/plugins/utils/httpRequest.js'
import { urlBase64ToUint8Array } from '@/plugins/utils/helpers.js'
import { DELETE_TILE_CACHE_EVENT } from '@/plugins/utils/events.js'
import { setGUIColor } from '@/plugins/utils/GuiColor.js'
import { ONLINE_MESSAGE_CHANGE_EVENT_TYPE } from '@/plugins/utils/events.js'

const { t } = useI18n();
const store = usePortAllData();
const { getOnline, getLayerColor } = storeToRefs(store)
const { setIsMobile, setMapView, setLocale, setVisibleLayers } = store
const $config = inject('$config')
const $map = inject('$map')

onBeforeMount(() => {
  setGUIColor(getLayerColor.value, getOnline.value)
})

onMounted(async () => {
  const isUpdated = localStorage.getItem('is-updated') === 'true' || false
  if (!isUpdated) {
    localStorage.clear()
    store.$reset()
    setMapView($config.viewOptions)
    const defaultLayers = $config.viewOptions.defaultLayers
    if (defaultLayers?.length) setVisibleLayers(defaultLayers)
    const storedLocale = localStorage.getItem('locale');
    if (!storedLocale) {
      const defaultLang = $config.viewOptions.defaultLang || 'en';
      setLocale(defaultLang);
    }
    alert('Please, reload you browser, and clear your browser cache')
    localStorage.setItem('is-updated', 'true')
  }

  window.addEventListener('resize', resizeEvent)
  setIsMobile(window.matchMedia("(max-width: 768px)").matches)
  window.addEventListener('contextmenu', (e) => {
    e.preventDefault()
  })

  document.addEventListener(ONLINE_MESSAGE_CHANGE_EVENT_TYPE, async (e) => {
      if (e.detail.online && e.detail.action === 'open') {
        await checkPortallDatabaseVersion()
      }
    })

  await registerServiceWorker()
  await checkPortallDatabaseVersion()
})

const checkPortallDatabaseVersion = async () => {
  let currentDatabaseVersion
  let oldDatabaseVersion
  if (localStorage.getItem('token')) {
    currentDatabaseVersion = await $map.getMapDatabaseVersion()
    oldDatabaseVersion = localStorage.getItem('mapDBVersion')
  } else return
  const portallVersion = localStorage.getItem('portall-version')
  const viteVersion = import.meta.env.VITE_VERSION
  const isDev = import.meta.env.VITE_SERVER_URL.startsWith('http://localhost')
  const isLastPortallVersion = portallVersion && portallVersion === viteVersion
  const isLastDatabaseVersion = currentDatabaseVersion && oldDatabaseVersion === currentDatabaseVersion
  if (!isDev && (!isLastPortallVersion || !isLastDatabaseVersion)) {
    const refreshCache = async () => {
      if (caches) {
        (await caches.keys()).map((key) => caches.delete(key))
        localStorage.setItem('portall-version', viteVersion)
        localStorage.setItem('mapDBVersion', currentDatabaseVersion)
        location.reload()
      }
    }
    toast(MsgToast, {
      dangerouslyHTMLString: true,
      closeOnClick: false,
      autoClose: false,
      toastStyle: {
        width: '550px'
      },
      data: {
        msg: t('new-version'),
        msgBis: t('new-version-bis'),
        refreshButton: t('refresh-button')
      },
      position: toast.POSITION.BOTTOM_LEFT,
      closeButton: false,
      onClose: () => refreshCache()
    })
  }
}

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeEvent)
})

const resizeEvent = () => {
  setIsMobile(window.matchMedia("(max-width: 768px)").matches)
}

const registerServiceWorker = async () => {
  let registration;
  if ('serviceWorker' in navigator) {
    registration = await navigator.serviceWorker.register('/sw.js')

    let preventDevToolsReloadLoop;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Ensure refresh is only called once.
      // This works around a bug in "force update on reload".
      if (preventDevToolsReloadLoop) return;
      preventDevToolsReloadLoop = true;
      window.location.reload();
    });
  }
  if ('Notification' in window && registration) {
    const permission = Notification.permission
    if (permission === 'default') {
      const newPerm = await Notification.requestPermission()
      if (newPerm === 'granted') await notifSubscribe(registration)
    } else if (permission === 'granted') {
      await notifSubscribe(registration)
    }
  }

  if (!navigator?.serviceWorker?.controller) {
    // The window client isn't currently controlled so it's a new service
    // worker that will activate immediately
    return;
  }

  if (registration) {
    onNewServiceWorker(registration, () => {
      registration.waiting.postMessage('skipWaiting');
      document.dispatchEvent(new CustomEvent(DELETE_TILE_CACHE_EVENT))
    });
  }
}

const onNewServiceWorker = (registration, callback) => {
  if (registration.waiting) {
    // SW is waiting to activate. Can occur if multiple clients open and
    // one of the clients is refreshed.
    return callback();
  }

  function listenInstalledStateChange() {
    registration.installing.addEventListener('statechange', (event) => {
      if (event.target.state === 'installed') {
        // A new service worker is available, inform the user
        callback();
      }
    });
  }

  if (registration.installing) {
    return listenInstalledStateChange();
  }

  // We are currently controlled so a new SW may be found...
  // Add a listener in case a new SW is found,
  registration.addEventListener('updatefound', listenInstalledStateChange);
}

const notifSubscribe = async (registration) => {
  if (localStorage.getItem('token')) {
    try {
      const { publicKey } = await httpRequest('/notification/publicKey', { method: 'GET' }, true, true)
      if (publicKey === 'NO_NOTIFICATION') return
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
          subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        })  
      }
      
      await httpRequest('/notification/subscribe', { method: 'POST', body: subscription }, true, false)
      console.log('Web push registration succeded!');
    } catch (e) {
      console.log('Web push is not available');
    }
  }
}

const logged = async () => {
  await registerServiceWorker()
  await checkPortallDatabaseVersion()
}

</script>

<template>
  <RouterView @logged="logged" />
</template>

<i18n src="@/locales/msgToast.json"></i18n>